import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import {
  MENU_PROBLEM_DESCRIPTIONS,
  MENU_PROBLEM_LABELS,
  MENU_PROBLEM_ORDER,
  menuProblemTone,
  safePublicMenuLink,
  type MenuProblemKind,
} from '../lib/menuProblemsView'

const API = import.meta.env.VITE_API_URL ?? ''

interface MenuProblem {
  competitor_id: string
  competitor_name: string
  organization_names: string[]
  tracked_locations: number
  website_url: string | null
  source_type: string | null
  kind: MenuProblemKind
  failure_label: string
  last_snapshot_at: string | null
  last_success_at: string | null
  last_checked_at: string | null
  last_attempt_at: string | null
  latest_request_status: string | null
  failed_attempts_7d: number
  customer_requested: boolean
  matching_failure_count: number
}

interface MenuProblemsReport {
  checked_at: string
  summary: Record<'total' | MenuProblemKind, number>
  items: MenuProblem[]
}

function formatWhen(value: string | null): string {
  if (!value) return 'Never'
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return 'Unknown'
  return date.toLocaleString([], {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function sourceLabel(value: string | null): string {
  if (!value) return 'Not verified'
  return value === 'jane' ? 'Jane' : value.charAt(0).toUpperCase() + value.slice(1)
}

const TONE_COLORS = {
  danger: { text: 'var(--accent-alert)', bg: 'rgba(212,83,126,0.10)', border: 'rgba(212,83,126,0.28)' },
  warning: { text: 'var(--accent-block)', bg: 'rgba(186,117,23,0.10)', border: 'rgba(186,117,23,0.28)' },
  positive: { text: 'var(--accent-intel)', bg: 'rgba(29,158,117,0.10)', border: 'rgba(29,158,117,0.28)' },
  neutral: { text: 'var(--text-2)', bg: 'var(--bg-2)', border: 'var(--border)' },
}

export default function MenuProblems() {
  const authFetch = useAuthFetch()
  const [report, setReport] = useState<MenuProblemsReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeKind, setActiveKind] = useState<MenuProblemKind | 'all'>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await authFetch(`${API}/api/v1/admin/reconciliation/menu-problems`)
      const payload = await response.json()
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || 'The menu status report was not available')
      }
      setReport(payload.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The menu status report was not available')
    } finally {
      setLoading(false)
    }
  }, [authFetch])

  useEffect(() => { void load() }, [load])

  const visibleItems = useMemo(() => {
    if (!report) return []
    const order = new Map(MENU_PROBLEM_ORDER.map((kind, index) => [kind, index]))
    return report.items
      .filter(item => activeKind === 'all' || item.kind === activeKind)
      .sort((a, b) => (order.get(a.kind) ?? 99) - (order.get(b.kind) ?? 99))
  }, [activeKind, report])

  if (loading) {
    return <div style={pageStyle}>Checking tracked menus and collection health…</div>
  }

  if (error || !report) {
    return (
      <div style={pageStyle}>
        <h1 style={headingStyle}>Menu Problems</h1>
        <p style={{ color: 'var(--accent-alert)', marginTop: 18 }}>
          Couldn't load the internal menu report: {error ?? 'No report returned'}
        </p>
        <button style={buttonStyle} onClick={() => void load()}>Retry report</button>
      </div>
    )
  }

  return (
    <div style={pageStyle} data-testid="menu-problems-page">
      <header style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <div style={eyebrowStyle}>INTERNAL OPERATIONS</div>
          <h1 style={headingStyle}>Menu Problems</h1>
          <p style={subtitleStyle}>
            Founder-only collection triage. Customers see only their menu availability—not these technical details.
          </p>
        </div>
        <button style={buttonStyle} onClick={() => void load()}>Refresh status</button>
      </header>

      <div style={policyStyle}>
        <strong style={{ color: 'var(--text-1)' }}>System-first rule:</strong>{' '}
        when several menus fail in the same pattern, investigate CannaSpy's collector or adapter before treating stores as unavailable.
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard label="All problems" value={report.summary.total} active={activeKind === 'all'} onClick={() => setActiveKind('all')} />
        {MENU_PROBLEM_ORDER.map(kind => (
          <SummaryCard
            key={kind}
            label={MENU_PROBLEM_LABELS[kind]}
            value={report.summary[kind]}
            active={activeKind === kind}
            onClick={() => setActiveKind(kind)}
          />
        ))}
      </div>

      {visibleItems.length === 0 ? (
        <div style={emptyStyle}>
          {report.summary.total === 0
            ? 'All actively tracked competitor menus are current.'
            : 'No menu problems match this filter.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {visibleItems.map(item => {
            const tone = TONE_COLORS[menuProblemTone(item.kind)]
            const websiteUrl = safePublicMenuLink(item.website_url)
            return (
              <article key={item.competitor_id} style={problemCardStyle} data-testid={`menu-problem-${item.kind}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <h2 style={itemHeadingStyle}>{item.competitor_name}</h2>
                      <span style={{ ...chipStyle, color: tone.text, background: tone.bg, borderColor: tone.border }}>
                        {MENU_PROBLEM_LABELS[item.kind]}
                      </span>
                      {item.customer_requested && <span style={requestChipStyle}>Customer requested access</span>}
                    </div>
                    <div style={metaStyle}>
                      {item.organization_names.join(', ') || 'Tracked account'} · {item.tracked_locations} tracked location{item.tracked_locations === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    {websiteUrl && (
                      <a href={websiteUrl} target="_blank" rel="noreferrer" style={linkButtonStyle}>Open public website ↗</a>
                    )}
                    {item.customer_requested && <Link to="/admin/reconciliation" style={linkButtonStyle}>Open review queue</Link>}
                  </div>
                </div>

                <p style={{ color: 'var(--text-2)', fontSize: 13, margin: '12px 0 0', lineHeight: 1.55 }}>
                  {MENU_PROBLEM_DESCRIPTIONS[item.kind]} {item.failure_label}.
                </p>

                <div style={detailGridStyle}>
                  <Detail label="Current source" value={sourceLabel(item.source_type)} />
                  <Detail label="Last successful menu" value={formatWhen(item.last_success_at ?? item.last_snapshot_at)} />
                  <Detail label="Last attempt" value={formatWhen(item.last_attempt_at ?? item.last_checked_at)} />
                  <Detail label="Failed attempts · 7 days" value={String(item.failed_attempts_7d)} />
                  {item.kind === 'likely_system' && (
                    <Detail label="Matching failures" value={String(item.matching_failure_count)} />
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}

      <div style={{ ...metaStyle, marginTop: 20 }} data-testid="menu-problems-report-checked">
        Report checked <time dateTime={report.checked_at}>{formatWhen(report.checked_at)}</time>
      </div>
    </div>
  )
}

function SummaryCard({ label, value, active, onClick }: { label: string; value: number; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ ...summaryCardStyle, borderColor: active ? 'var(--accent-intel)' : 'var(--border)' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 24, color: 'var(--text-1)' }}>{value}</span>
      <span style={{ color: 'var(--text-2)', fontSize: 11.5, textAlign: 'left', lineHeight: 1.3 }}>{label}</span>
    </button>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={detailLabelStyle}>{label}</div>
      <div style={{ color: 'var(--text-1)', fontFamily: 'var(--mono)', fontSize: 12, marginTop: 4 }}>{value}</div>
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
  boxSizing: 'border-box',
  padding: '28px 32px 48px',
  maxWidth: 1280,
  margin: '0 auto',
  color: 'var(--text-1)',
  fontFamily: 'var(--sans)',
}
const eyebrowStyle: React.CSSProperties = { fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.16em', color: 'var(--accent-intel)', marginBottom: 7 }
const headingStyle: React.CSSProperties = { fontFamily: 'var(--display)', fontSize: 34, fontWeight: 400, margin: 0, color: 'var(--text-1)' }
const subtitleStyle: React.CSSProperties = { color: 'var(--text-2)', fontSize: 13, margin: '7px 0 0', maxWidth: 720, lineHeight: 1.55 }
const buttonStyle: React.CSSProperties = { padding: '9px 13px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-1)', fontFamily: 'var(--sans)', cursor: 'pointer' }
const policyStyle: React.CSSProperties = { margin: '22px 0', padding: '12px 14px', border: '1px solid rgba(29,158,117,0.28)', borderRadius: 8, background: 'rgba(29,158,117,0.08)', color: 'var(--text-2)', fontSize: 13, lineHeight: 1.55 }
const summaryGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 10, marginBottom: 22 }
const summaryCardStyle: React.CSSProperties = { minHeight: 82, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-1)', cursor: 'pointer' }
const problemCardStyle: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-1)', padding: 16, borderTop: '2px solid var(--accent-block)' }
const itemHeadingStyle: React.CSSProperties = { fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text-1)' }
const chipStyle: React.CSSProperties = { fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '.04em', padding: '4px 7px', border: '1px solid', borderRadius: 999 }
const requestChipStyle: React.CSSProperties = { ...chipStyle, color: 'var(--accent-intel)', background: 'rgba(29,158,117,0.10)', borderColor: 'rgba(29,158,117,0.28)' }
const metaStyle: React.CSSProperties = { color: 'var(--text-3)', fontSize: 11.5, marginTop: 5 }
const linkButtonStyle: React.CSSProperties = { ...buttonStyle, fontSize: 11.5, textDecoration: 'none', display: 'inline-block' }
const detailGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginTop: 15, paddingTop: 13, borderTop: '1px solid var(--border-subtle)' }
const detailLabelStyle: React.CSSProperties = { color: 'var(--text-3)', fontSize: 9.5, fontFamily: 'var(--mono)', letterSpacing: '.1em', textTransform: 'uppercase' }
const emptyStyle: React.CSSProperties = { marginTop: 28, border: '1px solid var(--border)', borderRadius: 8, padding: 28, color: 'var(--text-2)', background: 'var(--bg-1)' }
